import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initGA, logPageView, initializeHpTracking } from './utils/analytics';
import { useSessionTracking } from './hooks/useSessionTracking';
import { SearchPage } from './pages/SearchPage';
import { SearchResult } from './pages/SearchResult';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import Home from './components/Home';
import VetsContact from './components/VetsContact';
import AboutUs from './components/AboutUs';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import VetSignup from './components/auth/VetSignup';
import GeneralSignup from './components/auth/GeneralSignup';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import PracticesList from './components/practices/PracticesList';
import PracticeDetail from './components/practices/PracticeDetail';
import PracticeForm from './components/practices/PracticeForm';
import PetOwnersList from './components/pet-owners/PetOwnersList';
import PetOwnerDetail from './components/pet-owners/PetOwnerDetail';
import PetOwnerForm from './components/pet-owners/PetOwnerForm';
import PetOwnerCreate from './components/pet-owners/PetOwnerCreate';
import PetDetail from './components/pets/PetDetail';
import PetForm from './components/pets/PetForm';
import MedicalRecordForm from './components/medical-records/MedicalRecordForm';
import MedicalRecordDetail from './components/medical-records/MedicalRecordDetail';
import MedicalRecordHistory from './components/medical-records/MedicalRecordHistory';
import VisitTranscriptDetail from './components/visit-transcripts/VisitTranscriptDetail';
import VisitTranscriptForm from './components/visit-transcripts/VisitTranscriptForm';
import VisitTranscriptRecorder from './components/visit-transcripts/VisitTranscriptRecorder';
import AppointmentPetRecorder from './components/visit-transcripts/AppointmentPetRecorder';
import AppointmentForm from './components/appointments/AppointmentForm';
import VetDashboardPage from './pages/VetDashboardPage';
import Dashboard from './pages/Dashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SmsConsent from './pages/SmsConsent';
import Pricing from './pages/Pricing';
import AIFrontDesk from './pages/AIFrontDesk';
import VisitTranscriptions from './pages/VisitTranscriptions';
import WebsiteHosting from './pages/WebsiteHosting';
import OnlineBooking from './pages/OnlineBooking';
import CalendarProduct from './pages/CalendarProduct';
import ProtoPIMS from './pages/ProtoPIMS';
import InventoryManagement from './pages/InventoryManagement';
import TwoWayTexting from './pages/TwoWayTexting';
import Unsubscribe from './pages/Unsubscribe';
import TrackingRedirect from './pages/TrackingRedirect';
import MobileForms from './pages/MobileForms';
import AcceptInvitation from './pages/AcceptInvitation';
import PracticeTeam from './pages/PracticeTeam';
import PendingInvitations from './pages/PendingInvitations';
import PracticeSelection from './pages/PracticeSelection';
import CreatePractice from './pages/CreatePractice';
import Settings from './pages/Settings';
import SetPassword from './pages/SetPassword';
import DocsPage from './pages/DocsPage';
import BookingWidget from './pages/BookingWidget';
import PublicFormView from './pages/PublicFormView';
import SeeItInAction from './pages/SeeItInAction';
import PracticeShow from './pages/PracticeShow';
import PracticesByState from './pages/PracticesByState';
import PracticesByCity from './pages/PracticesByCity';
import PracticesDirectory from './pages/PracticesDirectory';
import IPhoneApp from './pages/iPhoneApp';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import Encounter from './pages/Encounter';
import SignConsent from './pages/SignConsent';
import SignedConsentView from './pages/SignedConsentView';
import ChristmasCard from './pages/ChristmasCard';
import SolutionsGrow from './pages/SolutionsGrow';
import SolutionsRecover from './pages/SolutionsRecover';
import PaywallEnforcer from './components/common/PaywallEnforcer';
import PublicBookingPage from './pages/PublicBookingPage';
import OrganizationDashboard from './pages/OrganizationDashboard';
import { ChatDockProvider } from './contexts/ChatDockContext';

// Import auth utilities to set up fetch interceptor
import './utils/authUtils';

const ConditionalHeader = () => {
  const location = useLocation();
  // Don't show header on dashboard, settings, privacy, terms, docs, encounter, booking, or christmas card routes
  if (location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.includes('/encounter/') ||
    location.pathname.startsWith('/forms/') ||
    location.pathname.startsWith('/book/') ||
    location.pathname === '/privacy' ||
    location.pathname === '/terms' ||
    location.pathname === '/christmas' ||
    location.pathname.startsWith('/docs')) {
    return null;
  }
  return <Header />;
};

const PageViewTracker = () => {
  const location = useLocation();

  // Track page views for Google Analytics
  useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  // Track session data for sales campaigns
  useSessionTracking();

  return null;
};

const RedirectToDashboardPet = () => {
  const { uuid } = useParams();
  return <Navigate to={`/dashboard/pets/${uuid}`} replace />;
};

const RedirectToDashboardPetOwner = () => {
  const { uuid } = useParams();
  return <Navigate to={`/dashboard/pet_owners/${uuid}`} replace />;
};

const RedirectToDashboardPetOwnerEdit = () => {
  const { uuid } = useParams();
  return <Navigate to={`/dashboard/pet_owners/${uuid}/edit`} replace />;
};

const RedirectToDashboardMedicalRecordCreate = () => {
  const { petId } = useParams();
  return <Navigate to={`/dashboard/pets/${petId}/medical-records/create`} replace />;
};

const RedirectToDashboardMedicalRecordHistory = () => {
  const { petId } = useParams();
  return <Navigate to={`/dashboard/pets/${petId}/medical-records/history`} replace />;
};

const RedirectToDashboardMedicalRecordDetail = () => {
  const { petId, recordId } = useParams();
  return <Navigate to={`/dashboard/pets/${petId}/medical-records/${recordId}`} replace />;
};

const RedirectToDashboardMedicalRecordEdit = () => {
  const { petId, recordId } = useParams();
  return <Navigate to={`/dashboard/pets/${petId}/medical-records/${recordId}/edit`} replace />;
};

const RedirectToDashboardPetCreate = () => {
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('owner_id');
  return <Navigate to={`/dashboard/pets/create${ownerId ? `?owner_id=${ownerId}` : ''}`} replace />;
};

const RedirectToDashboardPetEdit = () => {
  const { uuid } = useParams();
  return <Navigate to={`/dashboard/pets/${uuid}/edit`} replace />;
};

const App = () => {
  useEffect(() => {
    initGA();
    initializeHpTracking(); // Track HP tag from URL for CRM
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ChatDockProvider>
            <ScrollToTop />
            <div className="min-h-screen bg-white dark:bg-dark-bg-primary transition-colors duration-200">
            <PageViewTracker />
            <ConditionalHeader />
            <PaywallEnforcer />
            <Routes>
            {/* Original Routes - RESTORED */}
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<VetsContact />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/see-it-in-action" element={<SeeItInAction />} />
            <Route path="/iphone-app" element={<IPhoneApp />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/voice-agents/getting-started" element={<DocsPage />} />
            <Route path="/products/ai-front-desk" element={<AIFrontDesk />} />
            <Route path="/products/visit-transcriptions" element={<VisitTranscriptions />} />
            <Route path="/products/website-hosting" element={<WebsiteHosting />} />
            <Route path="/products/online-booking" element={<OnlineBooking />} />
            <Route path="/products/calendar" element={<CalendarProduct />} />
            <Route path="/products/medical-records" element={<ProtoPIMS />} />
            <Route path="/products/inventory-management" element={<InventoryManagement />} />
            <Route path="/products/two-way-texting" element={<TwoWayTexting />} />
            <Route path="/products/mobile-forms" element={<MobileForms />} />
            <Route path="/solutions/grow" element={<SolutionsGrow />} />
            <Route path="/solutions/recover" element={<SolutionsRecover />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/sms-consent" element={<SmsConsent />} />

            {/* Public Forms Route */}
            <Route path="/forms/:submissionId" element={<PublicFormView />} />

            {/* Public Online Booking Route */}
            <Route path="/book/:state/:city/:slug" element={<PublicBookingPage />} />

            {/* Public Unsubscribe Route */}
            <Route path="/unsubscribe/:encodedEmail" element={<Unsubscribe />} />

            {/* Tracking Redirect Route - Clean shortcode URLs */}
            <Route path="/t/:shortcode" element={<TrackingRedirect />} />

            {/* Invitation Routes - Accessible to PENDING_INVITE */}
            <Route path="/accept-invite/:inviteId" element={<AcceptInvitation />} />
            <Route path="/pending-invitations" element={
              <RoleBasedRoute allowedRoles={['PENDING_INVITE', 'VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <PendingInvitations />
              </RoleBasedRoute>
            } />

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<GeneralSignup />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/signup/select-practice" element={
              <RoleBasedRoute allowedRoles={['PENDING_INVITE', 'VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <PracticeSelection />
              </RoleBasedRoute>
            } />
            <Route path="/signup/create-practice" element={
              <RoleBasedRoute allowedRoles={['PENDING_INVITE', 'VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <CreatePractice />
              </RoleBasedRoute>
            } />
            <Route path="/pet-owner-signup" element={<Signup />} />
            <Route path="/vet-signup" element={<VetSignup />} />

            {/* Settings Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/settings/*" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <Settings />
              </RoleBasedRoute>
            } />

            {/* Encounter Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/pet/:petId/encounter/:encounterId/*" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <Encounter />
              </RoleBasedRoute>
            } />

            {/* Organization Dashboard - For multi-practice management */}
            <Route path="/organization" element={
              <RoleBasedRoute allowedRoles={['ORG_ADMIN', 'VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <OrganizationDashboard />
              </RoleBasedRoute>
            } />

            {/* Dashboard Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/dashboard/*" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <Dashboard />
              </RoleBasedRoute>
            } />
            {/* Redirect old vet dashboard to new unified dashboard */}
            <Route path="/dashboard/vet" element={<Navigate to="/dashboard/appointments" replace />} />
            <Route path="/dashboard/vet/:date" element={<Navigate to="/dashboard/appointments" replace />} />

            {/* Appointment Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/appointments/new" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <AppointmentForm />
              </RoleBasedRoute>
            } />

            {/* Visit Transcript Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/visit-transcripts/record" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <VisitTranscriptRecorder />
              </RoleBasedRoute>
            } />
            <Route path="/visit-transcripts/record/:appointmentId" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <AppointmentPetRecorder />
              </RoleBasedRoute>
            } />

            {/* Practice Routes - Public */}
            <Route path="/practices" element={<PracticesDirectory />} />
            {/* Redirect old UUID-based practice URLs to practices directory */}
            <Route path="/practices/:id" element={<Navigate to="/practices" replace />} />

            {/* Public Practice Directory Routes (SEO-friendly) */}
            <Route path="/practice/:state/:city/:slug" element={<PracticeShow />} />
            <Route path="/practice/:state/:city" element={<PracticesByCity />} />
            <Route path="/practice/:state" element={<PracticesByState />} />

            {/* Practice Routes - Admin Only */}
            <Route path="/practices/new" element={
              <ProtectedRoute>
                <PracticeForm mode="create" />
              </ProtectedRoute>
            } />
            <Route path="/practices/:id/edit" element={
              <ProtectedRoute>
                <PracticeForm mode="edit" />
              </ProtectedRoute>
            } />

            {/* Practice Team Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/practice/team" element={
              <RoleBasedRoute allowedRoles={['VET_STAFF', 'PRACTICE_ADMIN', 'SYSTEM_ADMIN']}>
                <PracticeTeam />
              </RoleBasedRoute>
            } />

            {/* Pet Owner Routes - Redirect to dashboard */}
            <Route path="/pet_owners" element={<Navigate to="/dashboard/pet_owners" replace />} />
            <Route path="/pet_owners/new" element={<Navigate to="/dashboard/pet_owners/new" replace />} />
            <Route path="/pet_owners/:uuid" element={<RedirectToDashboardPetOwner />} />
            <Route path="/pet_owners/:uuid/edit" element={<RedirectToDashboardPetOwnerEdit />} />

            {/* Pet Routes - NOT accessible to PENDING_INVITE */}
            <Route path="/pets/create" element={<RedirectToDashboardPetCreate />} />
            <Route path="/pets/:uuid" element={<RedirectToDashboardPet />} />
            <Route path="/pets/:uuid/edit" element={<RedirectToDashboardPetEdit />} />

            {/* Medical Record Routes - Redirect to Dashboard */}
            <Route path="/pets/:petId/medical-records/create" element={<RedirectToDashboardMedicalRecordCreate />} />
            <Route path="/pets/:petId/medical-records/history" element={<RedirectToDashboardMedicalRecordHistory />} />
            <Route path="/pets/:petId/medical-records/:recordId" element={<RedirectToDashboardMedicalRecordDetail />} />
            <Route path="/pets/:petId/medical-records/:recordId/edit" element={<RedirectToDashboardMedicalRecordEdit />} />

            {/* Visit Transcript Routes */}
            <Route path="/pets/:petId/visit-transcripts/create" element={
              <ProtectedRoute>
                <VisitTranscriptForm mode="create" />
              </ProtectedRoute>
            } />
            <Route path="/pets/:petId/visit-transcripts/:transcriptId" element={
              <ProtectedRoute>
                <VisitTranscriptDetail />
              </ProtectedRoute>
            } />
            <Route path="/pets/:petId/visit-transcripts/:transcriptId/edit" element={
              <ProtectedRoute>
                <VisitTranscriptForm mode="edit" />
              </ProtectedRoute>
            } />

            {/* Protected RAG Search Routes */}
            <Route path="/rag" element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            } />
            <Route path="/rag/search" element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            } />
            <Route path="/rag/search/:searchId" element={
              <ProtectedRoute>
                <SearchResult />
              </ProtectedRoute>
            } />

            {/* Public Signing Route */}
            <Route path="/sign-consent/:token" element={<SignConsent />} />
            <Route path="/signed-consent/:token" element={<SignedConsentView />} />

            {/* Public Christmas Card */}
            <Route path="/christmas" element={<ChristmasCard />} />

            {/* 404 Catch-All Route - MUST BE LAST */}
            <Route path="*" element={<NotFound />} />
            </Routes>
            </div>
          </ChatDockProvider>
      </Router>
    </AuthProvider>
  </ThemeProvider>
  );
};

export default App;
